// ─────────────────────────────────────────────────────────────
// OpenMiscOrderListing.jsx
// Updated with modern UI + all existing logic preserved
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Plus,
  Search,
  Filter,
  X,
  Pencil,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Hourglass,
} from "lucide-react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const BASE_URL = "https://nissi-india.com/api/openPOList";

/* ─── Status badge helper ─── */
const StatusBadge = ({ status }) => {
  const map = {
    Waiting: {
      icon: <Hourglass size={12} />,
      cls: "bg-amber-100 text-amber-700 border-amber-200",
    },
    Closed: {
      icon: <CheckCircle size={12} />,
      cls: "bg-emerald-100 text-emerald-700 border-emerald-200",
    },
    Cancel: {
      icon: <XCircle size={12} />,
      cls: "bg-red-100 text-red-700 border-red-200",
    },
    Canceled: {
      icon: <XCircle size={12} />,
      cls: "bg-red-100 text-red-700 border-red-200",
    },
  };

  const cfg = map[status] || {
    icon: null,
    cls: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}
    >
      {cfg.icon} {status || "—"}
    </span>
  );
};

const OpenMiscOrderListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ── localStorage / session values ───────────────────────── */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchIdRaw =
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const storedBranch = localStorage.getItem("branch") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": storedBranchIdRaw,
      "x-branch": storedBranch,
      "x-userid": userId,
    }),
    [finYearId, finYear, storedBranchIdRaw, storedBranch, userId]
  );

  const storedBranchRaw = String(storedBranch || "").trim().toUpperCase();
  const branchtype =
    storedBranchRaw === "HEAD-OFFICE" ||
    storedBranchRaw === "HEAD OFFICE" ||
    storedBranchRaw === "HO"
      ? "HO"
      : "Branch";

  const branchId = Number(storedBranchIdRaw) || 0;

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  /* ── filter state ───────────────────────────────────────── */
  const [showFilters, setShowFilters] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [purchaseOrderNo, setPurchaseOrderNo] = useState("");
  const [supplier, setSupplier] = useState("");
  const [projectCodeId, setProjectCodeId] = useState("");
  const [status, setStatus] = useState("");

  /* ── dropdown data ──────────────────────────────────────── */
  const [supplierList, setSupplierList] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);

  /* ── grid state ─────────────────────────────────────────── */
  const [data, setData] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ── pagination ─────────────────────────────────────────── */
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_LIMIT = 10;

  /* ── load dropdowns on mount ────────────────────────────── */
  useEffect(() => {
    if (!branchId) return;

    const loadDropdowns = async () => {
      try {
        const [supplierRes, dropdownRes] = await Promise.all([
          axios.get(`${BASE_URL}/getSupplierNameByBranch`, {
            params: { branchid: branchId },
            headers: commonHeaders,
          }),
          axios.get(`${BASE_URL}/getDropdowns`, {
            params: { branchid: branchId, branchtype },
            headers: commonHeaders,
          }),
        ]);

        setSupplierList(supplierRes.data?.rows || []);
        setProjectCodes(dropdownRes.data?.data?.projectCodes || []);
      } catch (err) {
        console.error("Dropdown load failed", err);
        toast.error("Failed to load dropdowns");
      }
    };

    loadDropdowns();
  }, [branchId, branchtype, commonHeaders]);

  /* ── search ─────────────────────────────────────────────── */
  const handleSearch = async (targetPage = 1) => {
    if (!branchId) {
      toast.error("Branch not found in localStorage");
      return;
    }

    if (!fromDate) {
      toast.warning("Please select From Date");
      return;
    }

    if (!toDate) {
      toast.warning("Please select To Date");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    setLoading(true);
    setSearched(true);
    setPage(targetPage);

    try {
      const res = await axios.get(`${BASE_URL}/getOpenPurchaseOrderList`, {
        params: {
          branchid: branchId,
          pono: purchaseOrderNo || "",
          fromdate: fromDate,
          todate: toDate,
          supplierid: supplier || "",
          projectcodeid: projectCodeId || "",
          projectnameid: projectCodeId || "",
          status: status || "",
          page: targetPage,
          limit: PAGE_LIMIT,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setData(res.data.rows || []);
        setTotalCount(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      } else {
        setData([]);
        setTotalCount(0);
        setTotalPages(1);
        toast.error("Invalid response from server");
      }
    } catch (err) {
      console.error("Search failed", err);
      setData([]);
      setTotalCount(0);
      setTotalPages(1);
      toast.error(
        err?.response?.data?.message || "Failed to fetch purchase orders"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── clear ──────────────────────────────────────────────── */
  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setPurchaseOrderNo("");
    setSupplier("");
    setProjectCodeId("");
    setStatus("");
    setData([]);
    setSearched(false);
    setPage(1);
    setTotalPages(1);
    setTotalCount(0);
  };

  /* ── edit ───────────────────────────────────────────────── */
  const handleEdit = (row) => {
    localStorage.setItem("OpenPurchaseOrderEdit", "true");
    localStorage.setItem(
      "SelPurchaseOrderId",
      row.fld_Purchaseorderid || row.fld_purchaseorderid || ""
    );
    localStorage.setItem("SelPOStatus", row.Status || "");
    navigate("/inventory/OpenPurchaseOrderGST");
  };

  /* ── pagination ─────────────────────────────────────────── */
  const goToPage = (p) => {
    if (p >= 1 && p <= totalPages) {
      handleSearch(p);
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Open / Misc Purchase Order Listing
          </span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">
                  Open / Misc Purchase Orders
                </h1>
                <p
                  className={`text-sm ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Manage and track all open purchase orders
                </p>
              </div>
              {searched && (
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalCount} record{totalCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters((f) => !f)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${showFilters ? (isDark ? "bg-slate-800" : "bg-slate-100") : ""}`}
              >
                {showFilters ? <X size={16} /> : <Filter size={16} />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => {
                  localStorage.removeItem("OpenPurchaseOrderEdit");
                  localStorage.removeItem("SelPurchaseOrderId");
                  localStorage.removeItem("SelPOStatus");
                  navigate("/inventory/OpenPurchaseOrderGST");
                }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Plus size={16} />
                Add New Purchase Order
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                ></div>
                <h3 className="text-lg font-bold">Search Filters</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                <div>
                  <label className={labelClass}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className={inputClass}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className={inputClass}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass}>Purchase Order No</label>
                  <input
                    type="text"
                    placeholder="e.g. OP/01/0001"
                    className={inputClass}
                    value={purchaseOrderNo}
                    onChange={(e) => setPurchaseOrderNo(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass}>Supplier</label>
                  <select
                    className={inputClass}
                    value={supplier}
                    onChange={(e) => setSupplier(e.target.value)}
                  >
                    <option value="">All Suppliers</option>
                    {supplierList.map((s) => (
                      <option key={s.fld_supplierid} value={s.fld_supplierid}>
                        {s.fld_suppliername}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Project Code</label>
                  <select
                    className={inputClass}
                    value={projectCodeId}
                    onChange={(e) => setProjectCodeId(e.target.value)}
                  >
                    <option value="">All Projects</option>
                    {projectCodes.map((p, i) => (
                      <option
                        key={p.fld_projectid ?? p.fld_contractid ?? i}
                        value={
                          p.fld_projectid ??
                          p.fld_contractid ??
                          p.fld_projectcode ??
                          ""
                        }
                      >
                        {p.fld_projectcode ||
                          p.fld_contractname ||
                          p.fld_projectname ||
                          "Project"}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    className={inputClass}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="Waiting">Waiting</option>
                    <option value="Closed">Closed</option>
                    <option value="Cancel">Cancel</option>
                    <option value="Canceled">Canceled</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={handleClear}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <X size={16} />
                  Clear Filters
                </button>

                <button
                  onClick={() => handleSearch(1)}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass} disabled:opacity-50`}
                >
                  {loading ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Search size={16} />
                  )}
                  {loading ? "Searching..." : "Search Records"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
              ></div>
              <h3 className="text-xl font-bold">Purchase Order List</h3>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3 p-6">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`h-12 rounded-lg animate-pulse ${
                    isDark ? "bg-slate-700/50" : "bg-gray-100"
                  }`}
                />
              ))}
            </div>
          ) : data.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        PO No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Due Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Supplier
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Store
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Total Value
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Type
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    className={`divide-y ${
                      isDark ? "divide-gray-600" : "divide-gray-200"
                    }`}
                  >
                    {data.map((row, idx) => (
                      <tr
                        key={
                          row.fld_Purchaseorderid ||
                          row.fld_purchaseorderid ||
                          idx
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">
                          {(page - 1) * PAGE_LIMIT + idx + 1}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {row.fld_POno || row.fld_branchwiseno || "—"}
                          </span>
                        </td>

                        <td className="px-4 py-3">{row.fld_date || "—"}</td>
                        <td className="px-4 py-3">{row.fld_duedate || "—"}</td>
                        <td className="px-4 py-3 font-medium">
                          {row.fld_suppliername || "—"}
                        </td>
                        <td className="px-4 py-3">{row.fld_storename || "—"}</td>
                        <td className="px-4 py-3 font-semibold">
                          {row.fld_total != null
                            ? `₹ ${parseFloat(row.fld_total).toLocaleString(
                                "en-IN",
                                { minimumFractionDigits: 2 }
                              )}`
                            : "—"}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                              row.fld_type === "UM"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {row.fld_type === "UM" ? "Maintenance" : "Open PO"}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <StatusBadge status={row.Status} />
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleEdit(row)}
                            title="Edit"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 transition"
                          >
                            <Pencil size={14} />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <p
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Showing {(page - 1) * PAGE_LIMIT + 1}–
                    {Math.min(page * PAGE_LIMIT, totalCount)} of {totalCount}{" "}
                    records
                  </p>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className={`p-2 rounded-lg border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        isDark
                          ? "border-slate-600 hover:bg-slate-800"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                      const p = i + 1;
                      return (
                        <button
                          key={p}
                          onClick={() => goToPage(p)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                            page === p
                              ? `${buttonClass} text-white shadow`
                              : isDark
                              ? "border border-slate-600 hover:bg-slate-800 text-gray-300"
                              : "border border-gray-300 hover:bg-gray-50 text-gray-600"
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className={`p-2 rounded-lg border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                        isDark
                          ? "border-slate-600 hover:bg-slate-800"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <FileText size={48} className="text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {searched ? "No Records Found" : "Search to view purchase orders"}
              </h3>
              <p className="text-gray-500 text-center max-w-md text-sm">
                {searched
                  ? "Try adjusting your filters or date range."
                  : "Use the filters above and click Search Records."}
              </p>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default OpenMiscOrderListing;